import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { cx } from './cx'
import { Icon } from './Icon'
import { CircularProgress } from './Progress'
import { Tooltip } from './Tooltip'

export type ButtonVariant = 'filled' | 'tonal' | 'outlined' | 'text' | 'elevated'
/** `error` e' ammesso solo per la conferma distruttiva (bottone `text` dentro `useConfirm`/dialog). */
export type Tone = 'primary' | 'error'

interface CommonButtonProps {
  variant: ButtonVariant
  tone?: Tone | undefined
  /** md = Expressive S (40dp), sm = Expressive XS (32dp). */
  size?: 'md' | 'sm' | undefined
  icon?: string | undefined
  trailingIcon?: string | undefined
  fullWidth?: boolean | undefined
}

function buttonClass({ variant, tone = 'primary', size = 'md', fullWidth }: CommonButtonProps, className?: string) {
  return cx(
    'md-button',
    `md-button--${variant}`,
    `md-tone--${tone}`,
    size === 'sm' && 'md-button--sm',
    fullWidth && 'md-button--full',
    'md-state',
    'md-touch',
    className,
  )
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, CommonButtonProps {
  loading?: boolean | undefined
  /**
   * Marca un'azione di un Dialog che fa la stessa cosa della X (chiude senza altro effetto: "Chiudi", "Annulla").
   * A schermo intero (<600px) non e' mostrata nell'header: resta solo la X.
   */
  dismiss?: boolean | undefined
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, tone, size, icon, trailingIcon, loading, dismiss, fullWidth, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-dialog-dismiss={dismiss || undefined}
      className={buttonClass({ variant, tone, size, fullWidth }, className)}
    >
      {loading ? <CircularProgress size="sm" label="Operazione in corso" /> : icon ? <Icon name={icon} size={20} /> : null}
      {children}
      {trailingIcon && !loading ? <Icon name={trailingIcon} size={20} /> : null}
    </button>
  )
})

export interface ButtonLinkProps extends Omit<LinkProps, 'className'>, CommonButtonProps {
  className?: string | undefined
}

/** Link di react-router con l'aspetto di un bottone (niente ricarica della pagina). */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant, tone, size, icon, trailingIcon, fullWidth, className, children, ...rest },
  ref,
) {
  return (
    <Link ref={ref} {...rest} className={buttonClass({ variant, tone, size, fullWidth }, className)}>
      {icon ? <Icon name={icon} size={20} /> : null}
      {children}
      {trailingIcon ? <Icon name={trailingIcon} size={20} /> : null}
    </Link>
  )
})

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string
  /** Obbligatorio: un pulsante senza testo deve avere un nome accessibile. */
  'aria-label': string
  variant?: 'standard' | 'filled' | 'tonal' | 'outlined' | undefined
  tone?: Tone | undefined
  /** md = S (40dp, icona 24), sm = XS (32dp, icona 20; il bersaglio resta 48). */
  size?: 'md' | 'sm' | undefined
  /** Se definito il pulsante e' un toggle (aria-pressed). */
  selected?: boolean | undefined
  /**
   * Testo del tooltip. Di default e' l'`aria-label`; `false` lo disattiva.
   * Non usare `title`: il tooltip compare da solo.
   */
  tooltip?: string | false | undefined
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, variant = 'standard', tone = 'primary', size = 'md', selected, tooltip, title, className, type = 'button', ...rest },
  ref,
) {
  const label = rest['aria-label']
  // `title` (sconsigliato) diventa il testo del tooltip, cosi' non compaiono due suggerimenti.
  const tip = tooltip === false ? null : (tooltip ?? title ?? label)
  const button = (
    <button
      ref={ref}
      type={type}
      aria-pressed={selected}
      {...rest}
      title={tooltip === false ? title : undefined}
      className={cx(
        'md-icon-button',
        `md-icon-button--${variant}`,
        `md-tone--${tone}`,
        size === 'sm' && 'md-icon-button--sm',
        selected && 'md-icon-button--selected',
        'md-state',
        'md-touch',
        className,
      )}
    >
      <Icon name={icon} size={size === 'sm' ? 20 : 24} filled={selected} />
    </button>
  )
  return tip ? (
    <Tooltip label={tip} describe={tip !== label}>
      {button}
    </Tooltip>
  ) : (
    button
  )
})

interface IconButtonLinkCommon {
  icon: string
  /** Obbligatorio: un link senza testo deve avere un nome accessibile (e' anche il tooltip). */
  'aria-label': string
  variant?: 'standard' | 'filled' | 'tonal' | 'outlined' | undefined
  tone?: Tone | undefined
  size?: 'md' | 'sm' | undefined
  /** Testo del tooltip: di default l'`aria-label`; `false` lo disattiva. */
  tooltip?: string | false | undefined
  className?: string | undefined
}

/** Rotta del router: `<IconButtonLink to="/x/1/edit" icon="edit" aria-label="Modifica" size="sm" />`. */
export interface IconButtonRouteLinkProps extends IconButtonLinkCommon, Omit<LinkProps, 'className' | 'aria-label'> {
  to: LinkProps['to']
}

/** Indirizzo esterno (anteprima, download dall'API): stesso aspetto, <a href>. */
export interface IconButtonHrefLinkProps extends IconButtonLinkCommon, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'aria-label'> {
  href: string
  to?: never
}

export type IconButtonLinkProps = IconButtonRouteLinkProps | IconButtonHrefLinkProps

/** IconButton che naviga: react-router `Link` con `to`, oppure `<a>` con `href`. Stesse varianti e stesso tooltip. */
export const IconButtonLink = forwardRef<HTMLAnchorElement, IconButtonLinkProps>(function IconButtonLink(props, ref) {
  const { icon, variant = 'standard', tone = 'primary', size = 'md', tooltip, className, title, ...rest } = props
  const label = props['aria-label']
  const tip = tooltip === false ? null : (tooltip ?? title ?? label)
  const classes = cx(
    'md-icon-button',
    `md-icon-button--${variant}`,
    `md-tone--${tone}`,
    size === 'sm' && 'md-icon-button--sm',
    'md-state',
    'md-touch',
    className,
  )
  const glyph = <Icon name={icon} size={size === 'sm' ? 20 : 24} />
  const link =
    'to' in rest && rest.to !== undefined ? (
      <Link ref={ref} {...(rest as Omit<IconButtonRouteLinkProps, keyof IconButtonLinkCommon>)} title={tooltip === false ? title : undefined} className={classes}>
        {glyph}
      </Link>
    ) : (
      <a ref={ref} {...(rest as Omit<IconButtonHrefLinkProps, keyof IconButtonLinkCommon>)} title={tooltip === false ? title : undefined} className={classes}>
        {glyph}
      </a>
    )
  return tip ? (
    <Tooltip label={tip} describe={tip !== label}>
      {link}
    </Tooltip>
  ) : (
    link
  )
})
